import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as fabric from 'fabric';
import {
  deleteCanvasImageFromR2,
  generateCanvasImage,
  listCanvasProjectsFromR2,
  loadCanvasProjectFromR2,
  loadCanvasImagesFromR2,
  saveCanvasProjectToR2
} from '../../services/canvasAiService';
import {
  Check,
  Circle,
  CloudUpload,
  Copy,
  Crop,
  Download,
  Eraser,
  FolderOpen,
  Group as GroupIcon,
  Hand,
  Image as ImageIcon,
  Loader2,
  Maximize,
  Maximize2,
  Minus,
  Minimize2,
  Move,
  Pencil,
  Plus,
  RotateCcw,
  Redo2,
  Scissors,
  Sparkles,
  Square,
  Triangle,
  Trash2,
  Upload,
  Undo2,
  Ungroup,
  X,
  ZoomIn,
  ZoomOut
} from 'lucide-react';

const MIN_CROP_SIZE = 12;
const ANNOTATION_COLORS = ['#111827', '#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6'];
const PEN_SIZES = [2, 5, 10, 16];
const SHAPE_TOOLS = new Set(['line', 'rectangle', 'circle', 'triangle']);
const HISTORY_PROPERTIES = ['name', '__canvasHelper'];
const AI_IMAGE_SIZES = [
  { ratio: '16:9', size: '1536x864' },
  { ratio: '9:16', size: '864x1536' },
  { ratio: '1:1', size: '1024x1024' },
  { ratio: '5:4', size: '1280x1024' },
  { ratio: '4:5', size: '1024x1280' },
  { ratio: '4:3', size: '1344x1008' },
  { ratio: '3:4', size: '1008x1344' }
];
const AI_DESIGN_MODES = [
  { id: 'generate', label: 'Generate' },
  { id: 'edit', label: 'Edit' },
  { id: 'remove', label: 'Remove / replace' },
  { id: 'fill', label: 'Generative fill' },
  { id: 'expand', label: 'Expand' }
];
const AI_IMAGE_MODELS = [
  { value: 'gpt-image-2.5-flare', label: 'Flare · faster' },
  { value: 'gpt-image-2.5-sunburst', label: 'Sunburst · best quality' }
];
const AI_IMAGE_QUALITIES = ['medium', 'high', 'xhigh', 'max'];

const loadBrowserImage = (source) => new Promise((resolve, reject) => {
  const image = new window.Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('The image could not be decoded.'));
  image.src = source;
});

const serializeCanvas = (canvas) => {
  const snapshot = canvas.toJSON(HISTORY_PROPERTIES);
  snapshot.objects = snapshot.objects.filter((object) => !object.__canvasHelper);
  return JSON.stringify(snapshot);
};

const createId = () => (
  window.crypto?.randomUUID?.() || `asset-${Date.now()}-${Math.random().toString(16).slice(2)}`
);

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('An image in the project could not be prepared.'));
  reader.readAsDataURL(blob);
});

const embedCanvasImageSources = async (snapshot) => {
  const imageNodes = [];
  const visit = (value) => {
    if (!value || typeof value !== 'object') return;
    if (typeof value.src === 'string' && value.src && !value.src.startsWith('data:')) {
      imageNodes.push(value);
    }
    Object.values(value).forEach((child) => {
      if (child && typeof child === 'object') visit(child);
    });
  };
  visit(snapshot.objects);
  await Promise.all(imageNodes.map(async (node) => {
    const response = await fetch(node.src, { cache: 'no-store' });
    if (!response.ok) throw new Error('An image in the project could not be read.');
    node.src = await blobToDataUrl(await response.blob());
  }));
  return snapshot;
};

const getExportBounds = (objects) => {
  const bounds = objects.map((object) => object.getBoundingRect());
  const left = Math.floor(Math.min(...bounds.map((rect) => rect.left)));
  const top = Math.floor(Math.min(...bounds.map((rect) => rect.top)));
  const right = Math.ceil(Math.max(...bounds.map((rect) => rect.left + rect.width)));
  const bottom = Math.ceil(Math.max(...bounds.map((rect) => rect.top + rect.height)));

  return {
    left,
    top,
    width: Math.max(1, right - left),
    height: Math.max(1, bottom - top)
  };
};

const normalizeGeneratedImageSize = (source, requestedSize) => new Promise((resolve, reject) => {
  const [targetWidth, targetHeight] = requestedSize.split('x').map(Number);
  const image = new window.Image();
  image.onload = () => {
    if (image.naturalWidth === targetWidth && image.naturalHeight === targetHeight) {
      resolve(source);
      return;
    }

    const output = document.createElement('canvas');
    output.width = targetWidth;
    output.height = targetHeight;
    const context = output.getContext('2d');
    const scale = Math.max(
      targetWidth / image.naturalWidth,
      targetHeight / image.naturalHeight
    );
    const renderedWidth = image.naturalWidth * scale;
    const renderedHeight = image.naturalHeight * scale;
    context.drawImage(
      image,
      (targetWidth - renderedWidth) / 2,
      (targetHeight - renderedHeight) / 2,
      renderedWidth,
      renderedHeight
    );
    resolve(output.toDataURL('image/png'));
  };
  image.onerror = () => reject(new Error('The generated image could not be decoded.'));
  image.src = source;
});

const CanvasEditor = () => {
  const editorRef = useRef(null);
  const canvasElementRef = useRef(null);
  const canvasWrapperRef = useRef(null);
  const fileInputRef = useRef(null);
  const aiReferenceInputRef = useRef(null);
  const aiReferenceDropZoneRef = useRef(null);
  const aiLauncherRef = useRef(null);
  const drawToolbarRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const cropFrameRef = useRef(null);
  const cropTargetRef = useRef(null);
  const aiRegionRef = useRef(null);
  const aiTargetRef = useRef(null);
  const modeRef = useRef('select');
  const annotationToolRef = useRef('pen');
  const penSizeRef = useRef(5);
  const shapeStrokeColorRef = useRef('#ef4444');
  const shapeFillColorRef = useRef('#ffffff');
  const shapeFillTransparentRef = useRef(false);
  const erasingGestureRef = useRef(false);
  const erasedDuringGestureRef = useRef(false);
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const historyTimerRef = useRef(null);
  const isRestoringHistoryRef = useRef(false);
  const draggingCanvasImageRef = useRef(null);
  const canvasImageDragOriginRef = useRef(null);
  const canvasImageOverAiRef = useRef(false);
  const aiLauncherDragRef = useRef(null);
  const drawToolbarDragRef = useRef(null);
  const suppressAiLauncherClickRef = useRef(false);

  const [assets, setAssets] = useState([]);
  const [savedProjects, setSavedProjects] = useState([]);
  const [projectTitle, setProjectTitle] = useState('Untitled Canvas');
  const [currentProjectKey, setCurrentProjectKey] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [rotation, setRotation] = useState(0);
  const [mode, setMode] = useState('select');
  const [annotationTool, setAnnotationTool] = useState('pen');
  const [annotationColor, setAnnotationColor] = useState('#ef4444');
  const [shapeFillColor, setShapeFillColor] = useState('#ffffff');
  const [shapeFillTransparent, setShapeFillTransparent] = useState(false);
  const [penSize, setPenSize] = useState(5);
  const [historyStatus, setHistoryStatus] = useState({ canUndo: false, canRedo: false });
  const [zoom, setZoom] = useState(1);
  const [showAssets, setShowAssets] = useState(true);
  const [showSavedProjects, setShowSavedProjects] = useState(false);
  const [exportFormat, setExportFormat] = useState('png');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showTransparency, setShowTransparency] = useState(false);
  const [isRemovingBackground, setIsRemovingBackground] = useState(false);
  const [isAiPromptOpen, setIsAiPromptOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiDesignMode, setAiDesignMode] = useState('generate');
  const [aiImageSize, setAiImageSize] = useState('1024x1024');
  const [aiImageModel, setAiImageModel] = useState('gpt-image-2.5-flare');
  const [aiImageQuality, setAiImageQuality] = useState('high');
  const [aiImageBackground, setAiImageBackground] = useState('auto');
  const [aiImageVariations, setAiImageVariations] = useState(1);
  const [aiReferenceImage, setAiReferenceImage] = useState(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isSavingProject, setIsSavingProject] = useState(false);
  const [loadingProjectKey, setLoadingProjectKey] = useState(null);
  const [isLoadingR2Assets, setIsLoadingR2Assets] = useState(true);
  const [deletingAssetId, setDeletingAssetId] = useState(null);
  const [isDraggingCanvasImage, setIsDraggingCanvasImage] = useState(false);
  const [isCanvasImageOverAi, setIsCanvasImageOverAi] = useState(false);
  const [aiLauncherPosition, setAiLauncherPosition] = useState(null);
  const [drawToolbarPosition, setDrawToolbarPosition] = useState(null);
  const [backgroundRemovalProgress, setBackgroundRemovalProgress] = useState(0);
  const [message, setMessage] = useState('Upload an image or paste one to start designing.');

  useEffect(() => {
    let cancelled = false;
    const objectUrls = [];

    const loadSavedImages = async () => {
      try {
        const savedImages = await loadCanvasImagesFromR2();
        if (cancelled) return;
        const savedAssets = savedImages.map((image) => {
          const url = URL.createObjectURL(image.blob);
          objectUrls.push(url);
          return {
            id: `r2:${image.objectKey}`,
            name: image.name || 'Saved R2 image',
            url,
            r2ObjectKey: image.objectKey,
            uploaded: image.uploaded
          };
        });
        setAssets((current) => [
          ...savedAssets,
          ...current.filter((asset) => !savedAssets.some(({ id }) => id === asset.id))
        ]);
        if (savedAssets.length) {
          setMessage(`${savedAssets.length} saved image${savedAssets.length === 1 ? '' : 's'} loaded from Cloudflare R2.`);
        }
      } catch (error) {
        console.error('Unable to load saved R2 canvas images:', error);
        if (!cancelled) setMessage(error?.message || 'Saved R2 images could not be loaded.');
      } finally {
        if (!cancelled) setIsLoadingR2Assets(false);
      }
    };

    loadSavedImages();
    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    listCanvasProjectsFromR2()
      .then((projects) => {
        if (!cancelled) setSavedProjects(projects);
      })
      .catch((error) => {
        console.error('Unable to list saved canvas projects:', error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    annotationToolRef.current = annotationTool;
  }, [annotationTool]);

  useEffect(() => {
    penSizeRef.current = penSize;
  }, [penSize]);

  useEffect(() => {
    shapeStrokeColorRef.current = annotationColor;
  }, [annotationColor]);

  useEffect(() => {
    shapeFillColorRef.current = shapeFillColor;
  }, [shapeFillColor]);

  useEffect(() => {
    shapeFillTransparentRef.current = shapeFillTransparent;
  }, [shapeFillTransparent]);

  const updateHistoryStatus = useCallback(() => {
    setHistoryStatus({
      canUndo: historyIndexRef.current > 0,
      canRedo: historyIndexRef.current < historyRef.current.length - 1
    });
  }, []);

  const commitHistory = useCallback((canvas = fabricCanvasRef.current) => {
    if (!canvas || isRestoringHistoryRef.current) return;
    const snapshot = serializeCanvas(canvas);
    if (historyRef.current[historyIndexRef.current] === snapshot) return;
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    historyRef.current.push(snapshot);
    historyIndexRef.current = historyRef.current.length - 1;
    updateHistoryStatus();
  }, [updateHistoryStatus]);

  const scheduleHistoryCommit = useCallback((canvas) => {
    if (isRestoringHistoryRef.current || erasingGestureRef.current) return;
    window.clearTimeout(historyTimerRef.current);
    historyTimerRef.current = window.setTimeout(() => commitHistory(canvas), 80);
  }, [commitHistory]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const active = document.fullscreenElement === editorRef.current;
      setIsFullscreen(active);

      window.requestAnimationFrame(() => {
        const canvas = fabricCanvasRef.current;
        const wrapper = canvasWrapperRef.current;
        if (!canvas || !wrapper) return;
        canvas.setDimensions({
          width: Math.max(wrapper.clientWidth, 320),
          height: Math.max(wrapper.clientHeight, 440)
        });
        canvas.calcOffset();
        canvas.requestRenderAll();
      });
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const syncSelection = useCallback((canvas) => {
    const active = canvas.getActiveObject();
    if (!active || active.__canvasHelper) {
      setSelectedObject(null);
      return;
    }

    setSelectedObject(active);
    setDimensions({
      width: Math.max(1, Math.round(active.getScaledWidth())),
      height: Math.max(1, Math.round(active.getScaledHeight()))
    });
    setRotation(Math.round(active.angle || 0));
  }, []);

  useEffect(() => {
    if (!canvasElementRef.current || !canvasWrapperRef.current) return undefined;

    isRestoringHistoryRef.current = false;
    historyRef.current = [];
    historyIndexRef.current = -1;
    updateHistoryStatus();

    const wrapper = canvasWrapperRef.current;
    const canvas = new fabric.Canvas(canvasElementRef.current, {
      backgroundColor: 'transparent',
      preserveObjectStacking: true,
      selectionColor: 'rgba(16, 185, 129, 0.12)',
      selectionBorderColor: '#232751',
      selectionLineWidth: 1
    });
    canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
    canvas.freeDrawingBrush.color = '#ef4444';
    canvas.freeDrawingBrush.width = 5;
    fabricCanvasRef.current = canvas;

    const sizeCanvas = () => {
      canvas.setDimensions({
        width: Math.max(wrapper.clientWidth, 320),
        height: Math.max(wrapper.clientHeight, 440)
      });
      setAiLauncherPosition((current) => current ? {
        x: Math.min(current.x, Math.max(0, wrapper.clientWidth - 56)),
        y: Math.min(current.y, Math.max(0, wrapper.clientHeight - 56))
      } : current);
      setDrawToolbarPosition((current) => current ? {
        x: Math.min(current.x, Math.max(0, wrapper.clientWidth - (drawToolbarRef.current?.offsetWidth || 176))),
        y: Math.min(current.y, Math.max(0, wrapper.clientHeight - (drawToolbarRef.current?.offsetHeight || 420)))
      } : current);
      canvas.requestRenderAll();
    };

    const updateSelection = () => syncSelection(canvas);
    const queueHistoryUpdate = ({ target }) => {
      if (!target?.__canvasHelper) scheduleHistoryCommit(canvas);
    };
    canvas.on('selection:created', updateSelection);
    canvas.on('selection:updated', updateSelection);
    canvas.on('selection:cleared', updateSelection);
    canvas.on('object:modified', updateSelection);
    canvas.on('object:scaling', updateSelection);
    canvas.on('object:rotating', updateSelection);
    canvas.on('object:added', queueHistoryUpdate);
    canvas.on('object:removed', queueHistoryUpdate);
    canvas.on('object:modified', queueHistoryUpdate);
    canvas.on('path:created', ({ path }) => {
      if (!path) return;
      path.set({
        name: 'Annotation',
        strokeLineCap: 'round',
        strokeLineJoin: 'round'
      });
      path.setCoords();
      setMessage('Annotation added. Keep drawing or switch to Select to move it.');
    });

    sizeCanvas();
    commitHistory(canvas);
    const resizeObserver = new ResizeObserver(sizeCanvas);
    resizeObserver.observe(wrapper);

    return () => {
      resizeObserver.disconnect();
      window.clearTimeout(historyTimerRef.current);
      isRestoringHistoryRef.current = true;
      canvas.getObjects().forEach((object) => {
        if (object.__backgroundRemovalResultUrl) URL.revokeObjectURL(object.__backgroundRemovalResultUrl);
      });
      canvas.dispose();
      fabricCanvasRef.current = null;
    };
  }, [commitHistory, scheduleHistoryCommit, syncSelection, updateHistoryStatus]);

  const styleImageControls = (image) => {
    image.set({
      cornerColor: '#ffffff',
      cornerStrokeColor: '#232751',
      borderColor: '#232751',
      cornerSize: 10,
      transparentCorners: false,
      padding: 1,
      shadow: new fabric.Shadow({
        color: 'rgba(15, 23, 42, 0.22)',
        blur: 10,
        offsetX: 2,
        offsetY: 4
      })
    });
  };

  const restoreHistory = async (nextIndex, actionLabel) => {
    const canvas = fabricCanvasRef.current;
    const snapshot = historyRef.current[nextIndex];
    if (!canvas || !snapshot || isRestoringHistoryRef.current) return;

    window.clearTimeout(historyTimerRef.current);
    isRestoringHistoryRef.current = true;
    canvas.isDrawingMode = false;
    canvas.discardActiveObject();
    if (modeRef.current === 'crop') {
      cropFrameRef.current = null;
      cropTargetRef.current = null;
      modeRef.current = 'select';
      setMode('select');
    }

    try {
      await canvas.loadFromJSON(snapshot);
      canvas.getObjects().forEach((object) => {
        if (object instanceof fabric.FabricImage) styleImageControls(object);
      });
      historyIndexRef.current = nextIndex;
      updateHistoryStatus();
      syncSelection(canvas);
      canvas.requestRenderAll();
      setMessage(`${actionLabel} complete.`);
    } catch (error) {
      console.error(`Unable to ${actionLabel.toLowerCase()} canvas history:`, error);
      setMessage(`${actionLabel} could not be completed.`);
    } finally {
      isRestoringHistoryRef.current = false;
      canvas.isDrawingMode = modeRef.current === 'annotate' && annotationToolRef.current === 'pen';
    }
  };

  const undoCanvas = () => {
    if (historyIndexRef.current > 0) restoreHistory(historyIndexRef.current - 1, 'Undo');
  };

  const redoCanvas = () => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      restoreHistory(historyIndexRef.current + 1, 'Redo');
    }
  };

  const addImageToCanvas = useCallback(async (source, name = 'Image') => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    try {
      const image = await fabric.FabricImage.fromURL(source, { crossOrigin: 'anonymous' });
      const availableWidth = Math.max(180, (canvas.width || 700) * 0.62);
      const availableHeight = Math.max(180, (canvas.height || 500) * 0.68);
      const scale = Math.min(
        1,
        availableWidth / (image.width || availableWidth),
        availableHeight / (image.height || availableHeight)
      );

      image.set({
        left: ((canvas.width || availableWidth) - ((image.width || 0) * scale)) / 2,
        top: ((canvas.height || availableHeight) - ((image.height || 0) * scale)) / 2,
        scaleX: scale,
        scaleY: scale,
        name
      });
      styleImageControls(image);
      canvas.add(image);
      canvas.setActiveObject(image);
      canvas.requestRenderAll();
      syncSelection(canvas);
      setMode('select');
      setMessage('Image added. Drag the handles to resize, or choose Crop edges or Snip.');
    } catch (error) {
      console.error('Unable to add image to canvas:', error);
      setMessage('That image could not be opened. Try a PNG or JPG from your device.');
    }
  }, [syncSelection]);

  const importFile = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) {
      setMessage('Please choose an image file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result;
      const asset = { id: createId(), name: file.name || 'Uploaded image', url };
      setAssets((current) => [asset, ...current]);
      addImageToCanvas(url, asset.name);
    };
    reader.onerror = () => setMessage('The selected image could not be read.');
    reader.readAsDataURL(file);
  }, [addImageToCanvas]);

  const removeAsset = async (asset) => {
    if (!asset || deletingAssetId) return;
    setDeletingAssetId(asset.id);
    try {
      if (asset.r2ObjectKey) await deleteCanvasImageFromR2(asset.r2ObjectKey);
      setAssets((current) => current.filter(({ id }) => id !== asset.id));
      setMessage(`${asset.name || 'Image'} removed from Assets.`);
    } catch (error) {
      console.error('Unable to remove canvas asset:', error);
      setMessage(error?.message || 'The image could not be removed from Assets.');
    } finally {
      setDeletingAssetId(null);
    }
  };

  const clearAiRegion = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (canvas && aiRegionRef.current) canvas.remove(aiRegionRef.current);
    aiRegionRef.current = null;
    canvas?.requestRenderAll();
  }, []);

  const getAiTargetImage = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return null;
    const active = canvas.getActiveObject();
    if (active instanceof fabric.FabricImage) return active;
    if (
      aiTargetRef.current instanceof fabric.FabricImage &&
      canvas.getObjects().includes(aiTargetRef.current)
    ) return aiTargetRef.current;
    return null;
  }, []);

  const chooseClosestAiSize = useCallback((image) => {
    const bounds = image?.getBoundingRect();
    if (!bounds?.width || !bounds?.height) return;
    const ratio = bounds.width / bounds.height;
    const closest = AI_IMAGE_SIZES.reduce((best, candidate) => {
      const [width, height] = candidate.size.split('x').map(Number);
      const distance = Math.abs(Math.log(ratio / (width / height)));
      return !best || distance < best.distance ? { ...candidate, distance } : best;
    }, null);
    if (closest) setAiImageSize(closest.size);
  }, []);

  const selectAiDesignMode = useCallback((nextMode) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const existingTarget = getAiTargetImage();
    clearAiRegion();
    setAiDesignMode(nextMode);

    if (nextMode === 'generate') {
      aiTargetRef.current = null;
      setMessage('Describe a new image, optionally with a reference image.');
      return;
    }

    if (!existingTarget) {
      aiTargetRef.current = null;
      setMessage('Select an image on the canvas, then choose this AI tool again.');
      return;
    }

    if (nextMode === 'fill' && Math.abs(existingTarget.angle || 0) % 360 !== 0) {
      aiTargetRef.current = null;
      setMessage('Set the selected image rotation to 0° before using Generative fill.');
      return;
    }

    aiTargetRef.current = existingTarget;
    chooseClosestAiSize(existingTarget);
    if (nextMode === 'fill') {
      const bounds = existingTarget.getBoundingRect();
      const frame = new fabric.Rect({
        left: bounds.left + bounds.width * 0.25,
        top: bounds.top + bounds.height * 0.25,
        width: Math.max(MIN_CROP_SIZE, bounds.width * 0.5),
        height: Math.max(MIN_CROP_SIZE, bounds.height * 0.5),
        fill: 'rgba(245, 158, 11, 0.2)',
        stroke: '#fbbf24',
        strokeWidth: 2 / canvas.getZoom(),
        strokeDashArray: [8, 5],
        cornerColor: '#fbbf24',
        cornerStrokeColor: '#111827',
        cornerSize: 12,
        transparentCorners: false,
        lockRotation: true,
        hasRotatingPoint: false,
        name: 'AI edit region'
      });
      frame.__canvasHelper = true;
      aiRegionRef.current = frame;
      canvas.add(frame);
      canvas.setActiveObject(frame);
      canvas.requestRenderAll();
      setMessage('Resize the amber region over the area to change, then describe the edit.');
      return;
    }

    canvas.setActiveObject(existingTarget);
    canvas.requestRenderAll();
    const labels = {
      edit: 'Describe how the selected image should change.',
      remove: 'Name what to remove or what should replace it.',
      expand: 'Choose a larger aspect ratio and describe the surrounding scene.'
    };
    setMessage(labels[nextMode]);
  }, [chooseClosestAiSize, clearAiRegion, getAiTargetImage]);

  const canvasImageToDataUrl = useCallback((image) => {
    const longestEdge = Math.max(image.getScaledWidth(), image.getScaledHeight(), 1);
    const dataUrl = image.toDataURL({
      format: 'png',
      multiplier: Math.min(4, 2048 / longestEdge),
      withoutShadow: true
    });
    if (dataUrl.length > 20 * 1024 * 1024) {
      throw new Error('The selected image is too large for an AI edit. Resize or crop it first.');
    }
    return dataUrl;
  }, []);

  const createGenerativeFillInput = useCallback(async (target, region) => {
    const source = canvasImageToDataUrl(target);
    const sourceImage = await loadBrowserImage(source);
    const targetBounds = target.getBoundingRect();
    const regionBounds = region.getBoundingRect();
    const left = Math.max(targetBounds.left, regionBounds.left);
    const top = Math.max(targetBounds.top, regionBounds.top);
    const right = Math.min(targetBounds.left + targetBounds.width, regionBounds.left + regionBounds.width);
    const bottom = Math.min(targetBounds.top + targetBounds.height, regionBounds.top + regionBounds.height);
    if (right - left < MIN_CROP_SIZE || bottom - top < MIN_CROP_SIZE) {
      throw new Error('Place the amber edit region over the selected image.');
    }

    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = sourceImage.naturalWidth;
    maskCanvas.height = sourceImage.naturalHeight;
    const context = maskCanvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, maskCanvas.width, maskCanvas.height);
    const maskLeft = ((left - targetBounds.left) / targetBounds.width) * maskCanvas.width;
    const maskTop = ((top - targetBounds.top) / targetBounds.height) * maskCanvas.height;
    const maskWidth = ((right - left) / targetBounds.width) * maskCanvas.width;
    const maskHeight = ((bottom - top) / targetBounds.height) * maskCanvas.height;
    context.clearRect(
      Math.max(0, Math.floor(maskLeft)),
      Math.max(0, Math.floor(maskTop)),
      Math.min(maskCanvas.width, Math.ceil(maskWidth)),
      Math.min(maskCanvas.height, Math.ceil(maskHeight))
    );
    return { source, mask: maskCanvas.toDataURL('image/png') };
  }, [canvasImageToDataUrl]);

  const createExpandedEditInput = useCallback(async (target, size) => {
    const source = canvasImageToDataUrl(target);
    const sourceImage = await loadBrowserImage(source);
    const [outputWidth, outputHeight] = size.split('x').map(Number);
    const scale = Math.min(
      (outputWidth * 0.72) / sourceImage.naturalWidth,
      (outputHeight * 0.72) / sourceImage.naturalHeight
    );
    const width = Math.max(1, Math.round(sourceImage.naturalWidth * scale));
    const height = Math.max(1, Math.round(sourceImage.naturalHeight * scale));
    const left = Math.round((outputWidth - width) / 2);
    const top = Math.round((outputHeight - height) / 2);

    const sourceCanvas = document.createElement('canvas');
    sourceCanvas.width = outputWidth;
    sourceCanvas.height = outputHeight;
    sourceCanvas.getContext('2d').drawImage(sourceImage, left, top, width, height);

    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = outputWidth;
    maskCanvas.height = outputHeight;
    const maskContext = maskCanvas.getContext('2d');
    maskContext.fillStyle = '#ffffff';
    maskContext.fillRect(left, top, width, height);
    return {
      source: sourceCanvas.toDataURL('image/png'),
      mask: maskCanvas.toDataURL('image/png'),
      contentBox: { width, height, outputWidth, outputHeight }
    };
  }, [canvasImageToDataUrl]);

  const replaceCanvasImageWithAi = useCallback(async (target, source, name, expandedContentBox = null) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !target || !canvas.getObjects().includes(target)) return null;
    const bounds = target.getBoundingRect();
    const center = target.getCenterPoint();
    const index = canvas.getObjects().indexOf(target);
    const image = await fabric.FabricImage.fromURL(source, { crossOrigin: 'anonymous' });
    let displayWidth = bounds.width;
    let displayHeight = bounds.height;
    if (expandedContentBox) {
      const pixelScale = Math.min(
        bounds.width / expandedContentBox.width,
        bounds.height / expandedContentBox.height
      );
      displayWidth = expandedContentBox.outputWidth * pixelScale;
      displayHeight = expandedContentBox.outputHeight * pixelScale;
    }
    image.set({
      left: center.x - displayWidth / 2,
      top: center.y - displayHeight / 2,
      scaleX: displayWidth / (image.width || displayWidth),
      scaleY: displayHeight / (image.height || displayHeight),
      angle: 0,
      name
    });
    styleImageControls(image);
    canvas.discardActiveObject();
    canvas.remove(target);
    canvas.insertAt(Math.max(0, index), image);
    canvas.setActiveObject(image);
    canvas.requestRenderAll();
    aiTargetRef.current = image;
    syncSelection(canvas);
    commitHistory(canvas);
    return image;
  }, [commitHistory, syncSelection]);

  const generateImageWithAi = async (event) => {
    event.preventDefault();
    const prompt = aiPrompt.trim();
    if (prompt.length < 3) {
      setMessage('Describe what you want the AI Design Studio to create or change.');
      return;
    }

    const selectedSize = AI_IMAGE_SIZES.find(({ size }) => size === aiImageSize) || AI_IMAGE_SIZES[2];
    const target = aiDesignMode === 'generate' ? null : getAiTargetImage();
    if (aiDesignMode !== 'generate' && !target) {
      setMessage('Select an image on the canvas before using this AI tool.');
      return;
    }
    if (aiDesignMode === 'fill' && !aiRegionRef.current) {
      setMessage('Choose Generative fill again to create an editable region.');
      return;
    }

    setIsGeneratingImage(true);
    setMessage(`AI Design Studio is ${aiDesignMode === 'generate' ? 'creating' : 'editing'} your image...`);
    try {
      let sourceImage = null;
      let mask = null;
      let expandedContentBox = null;
      if (target && aiDesignMode === 'fill') {
        ({ source: sourceImage, mask } = await createGenerativeFillInput(target, aiRegionRef.current));
      } else if (target && aiDesignMode === 'expand') {
        ({ source: sourceImage, mask, contentBox: expandedContentBox } = await createExpandedEditInput(target, selectedSize.size));
      } else if (target) {
        sourceImage = canvasImageToDataUrl(target);
      }

      const [ratioWidth, ratioHeight] = selectedSize.ratio.split(':').map(Number);
      const orientation = ratioWidth === ratioHeight
        ? 'square'
        : ratioWidth > ratioHeight ? 'landscape' : 'portrait';
      const instructions = {
        generate: `Create this image: ${prompt}`,
        edit: `Edit the first image as follows: ${prompt}. Preserve every detail that was not requested to change.`,
        remove: `Edit the first image. ${prompt}. Remove or replace only the specified subject and reconstruct the surrounding area naturally. Preserve everything else.`,
        fill: `Edit only the transparent masked region of the first image: ${prompt}. Preserve everything outside the mask exactly.`,
        expand: `Extend the first image naturally into the surrounding transparent canvas: ${prompt}. Preserve the original center content and continue its lighting, perspective, texture, and scene.`
      };
      const framedPrompt = [
        instructions[aiDesignMode],
        '',
        `Required output: exact ${selectedSize.ratio} ${orientation} composition with no added border.`
      ].join('\n');
      const referenceImages = [
        ...(sourceImage ? [sourceImage] : []),
        ...(aiReferenceImage?.dataUrl ? [aiReferenceImage.dataUrl] : [])
      ];
      const result = await generateCanvasImage({
        prompt: framedPrompt,
        size: selectedSize.size,
        referenceImages,
        mask,
        model: aiImageModel,
        quality: aiImageQuality,
        background: aiImageBackground,
        variations: aiImageVariations
      });
      const generatedImages = (result?.images?.length ? result.images : [result?.imageBase64])
        .filter(Boolean);
      if (!generatedImages.length) throw new Error('The generated image was empty.');

      const imageUrls = await Promise.all(generatedImages.map(async (imageBase64) => {
        const returnedImageUrl = `data:${result.mimeType || 'image/png'};base64,${imageBase64}`;
        return normalizeGeneratedImageSize(returnedImageUrl, selectedSize.size);
      }));
      const modeLabel = AI_DESIGN_MODES.find(({ id }) => id === aiDesignMode)?.label || 'AI image';
      const generatedAssets = imageUrls.map((url, index) => ({
        id: createId(),
        name: `${modeLabel}${imageUrls.length > 1 ? ` ${index + 1}` : ''}: ${prompt.slice(0, 52)}`,
        url
      }));
      setAssets((current) => [...generatedAssets, ...current]);

      if (target) {
        clearAiRegion();
        await replaceCanvasImageWithAi(
          target,
          generatedAssets[0].url,
          generatedAssets[0].name,
          aiDesignMode === 'expand' ? expandedContentBox : null
        );
      } else {
        await addImageToCanvas(generatedAssets[0].url, generatedAssets[0].name);
      }
      setAiPrompt('');
      setAiReferenceImage(null);
      setMessage(`${generatedAssets.length} ${generatedAssets.length === 1 ? 'result' : 'variations'} created. The first result is on the canvas; all results are in Assets.`);
    } catch (error) {
      console.error('Unable to generate a Canvas image:', error);
      const errorMessage = `${error?.message || ''}`
        .replace(/^Firebase:\s*/i, '')
        .replace(/^functions\/[^:]+:\s*/i, '');
      setMessage(errorMessage || 'The AI image could not be generated. Please try again.');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const selectAiReferenceImage = (file) => {
    if (!file) return;
    const supportedTypes = new Set(['image/png', 'image/jpeg', 'image/webp']);
    if (!supportedTypes.has(file.type)) {
      setMessage('Choose a PNG, JPG, or WebP reference image.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMessage('Reference images must be 10 MB or smaller.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setAiReferenceImage({
        name: file.name || 'Reference image',
        dataUrl: reader.result
      });
      setMessage('Reference image attached to the AI prompt.');
    };
    reader.onerror = () => setMessage('The reference image could not be read.');
    reader.readAsDataURL(file);
  };

  const attachCanvasImageAsReference = useCallback((image) => {
    if (!(image instanceof fabric.FabricImage)) return false;
    try {
      const longestEdge = Math.max(image.getScaledWidth(), image.getScaledHeight(), 1);
      const dataUrl = image.toDataURL({
        format: 'png',
        multiplier: Math.min(4, 1536 / longestEdge),
        withoutShadow: true
      });
      if (dataUrl.length > 20 * 1024 * 1024) {
        setMessage('That canvas image is too large to use as an AI reference.');
        return false;
      }
      setAiReferenceImage({
        name: image.name || 'Canvas reference image',
        dataUrl
      });
      setIsAiPromptOpen(true);
      setMessage('Canvas image attached as the AI reference.');
      return true;
    } catch (error) {
      console.error('Unable to use the canvas image as a reference:', error);
      setMessage('That canvas image could not be attached as a reference.');
      return false;
    }
  }, []);

  useEffect(() => {
    const handlePaste = (event) => {
      const imageItem = Array.from(event.clipboardData?.items || [])
        .find((item) => item.type.startsWith('image/'));
      if (imageItem) importFile(imageItem.getAsFile());
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [importFile]);

  const cancelCrop = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (canvas && cropFrameRef.current) canvas.remove(cropFrameRef.current);
    if (cropTargetRef.current) {
      cropTargetRef.current.set({ selectable: true, evented: true });
      cropTargetRef.current.setCoords();
    }
    cropFrameRef.current = null;
    cropTargetRef.current = null;
    setMode('select');
    canvas?.discardActiveObject();
    canvas?.requestRenderAll();
  }, []);

  const startCrop = () => {
    const canvas = fabricCanvasRef.current;
    const image = canvas?.getActiveObject();
    if (!canvas || !(image instanceof fabric.FabricImage)) {
      setMessage('Select an image before starting an edge crop.');
      return;
    }
    if (Math.abs(image.angle || 0) % 360 !== 0) {
      setMessage('Set rotation to 0° before cropping this image.');
      return;
    }

    if (cropFrameRef.current) canvas.remove(cropFrameRef.current);
    const bounds = image.getBoundingRect();
    const frame = new fabric.Rect({
      left: bounds.left,
      top: bounds.top,
      width: bounds.width,
      height: bounds.height,
      fill: 'rgba(15, 23, 42, 0.08)',
      stroke: '#232751',
      strokeWidth: 2 / canvas.getZoom(),
      strokeDashArray: [8, 6],
      cornerColor: '#232751',
      cornerStrokeColor: '#ffffff',
      cornerSize: 12,
      transparentCorners: false,
      lockRotation: true,
      hasRotatingPoint: false
    });
    frame.__canvasHelper = true;
    cropFrameRef.current = frame;
    cropTargetRef.current = image;
    image.set({ selectable: false, evented: false });
    canvas.add(frame);
    canvas.setActiveObject(frame);
    canvas.requestRenderAll();
    setMode('crop');
    setMessage('Drag the crop frame’s edges, then choose Apply crop.');
  };

  const applyCrop = () => {
    const canvas = fabricCanvasRef.current;
    const image = cropTargetRef.current;
    const frame = cropFrameRef.current;
    if (!canvas || !image || !frame) return;

    const imageBounds = image.getBoundingRect();
    const frameBounds = frame.getBoundingRect();
    const left = Math.max(imageBounds.left, frameBounds.left);
    const top = Math.max(imageBounds.top, frameBounds.top);
    const right = Math.min(imageBounds.left + imageBounds.width, frameBounds.left + frameBounds.width);
    const bottom = Math.min(imageBounds.top + imageBounds.height, frameBounds.top + frameBounds.height);

    if (right - left < MIN_CROP_SIZE || bottom - top < MIN_CROP_SIZE) {
      setMessage('Keep at least a small visible area inside the image.');
      return;
    }

    const scaleX = Math.abs(image.scaleX || 1);
    const scaleY = Math.abs(image.scaleY || 1);
    image.set({
      cropX: (image.cropX || 0) + ((left - imageBounds.left) / scaleX),
      cropY: (image.cropY || 0) + ((top - imageBounds.top) / scaleY),
      width: (right - left) / scaleX,
      height: (bottom - top) / scaleY,
      left,
      top,
      selectable: true,
      evented: true
    });
    image.setCoords();
    canvas.remove(frame);
    cropFrameRef.current = null;
    cropTargetRef.current = null;
    canvas.setActiveObject(image);
    canvas.requestRenderAll();
    setMode('select');
    syncSelection(canvas);
    scheduleHistoryCommit(canvas);
    setMessage('Crop applied. You can continue resizing or crop again.');
  };

  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return undefined;

    let dragging = false;
    let spaceDown = false;
    let lastX = 0;
    let lastY = 0;
    let snipRectangle = null;
    let snipStart = null;
    let shapeObject = null;
    let shapeStart = null;

    const setPanCursor = (cursor) => {
      canvas.defaultCursor = cursor;
      canvas.hoverCursor = cursor;
      canvas.setCursor(cursor);
    };

    const isOverAiDropTarget = (nativeEvent) => {
      const pointer = nativeEvent?.changedTouches?.[0] || nativeEvent?.touches?.[0] || nativeEvent;
      if (!pointer) return false;
      return [aiReferenceDropZoneRef.current, aiLauncherRef.current]
        .filter(Boolean)
        .some((target) => {
          const bounds = target.getBoundingClientRect();
          return pointer.clientX >= bounds.left && pointer.clientX <= bounds.right &&
            pointer.clientY >= bounds.top && pointer.clientY <= bounds.bottom;
        });
    };

    const resetCanvasImageDrag = () => {
      draggingCanvasImageRef.current = null;
      canvasImageDragOriginRef.current = null;
      canvasImageOverAiRef.current = false;
      setIsDraggingCanvasImage(false);
      setIsCanvasImageOverAi(false);
    };

    const onObjectMoving = (event) => {
      if (!(event.target instanceof fabric.FabricImage)) return;
      if (draggingCanvasImageRef.current !== event.target && !canvasImageDragOriginRef.current) {
        const original = event.transform?.original;
        canvasImageDragOriginRef.current = {
          left: original?.left ?? event.target.left,
          top: original?.top ?? event.target.top
        };
      }
      draggingCanvasImageRef.current = event.target;
      const isOver = isOverAiDropTarget(event.e);
      canvasImageOverAiRef.current = isOver;
      setIsDraggingCanvasImage(true);
      setIsCanvasImageOverAi(isOver);
    };

    const onObjectModified = (event) => {
      if (draggingCanvasImageRef.current !== event.target) return;
      const shouldAttach = canvasImageOverAiRef.current || isOverAiDropTarget(event.e);
      if (shouldAttach) {
        const origin = canvasImageDragOriginRef.current;
        if (origin) {
          event.target.set(origin);
          event.target.setCoords();
          canvas.requestRenderAll();
          syncSelection(canvas);
        }
        attachCanvasImageAsReference(event.target);
      }
      resetCanvasImageDrag();
    };

    const eraseAnnotationAt = (nativeEvent) => {
      const point = canvas.getViewportPoint(nativeEvent);
      const previousTolerance = canvas.targetFindTolerance;
      canvas.targetFindTolerance = Math.max(6, penSizeRef.current);
      const annotation = [...canvas.getObjects()].reverse().find((object) => (
        object.name === 'Annotation' && !canvas.isTargetTransparent(object, point.x, point.y)
      ));
      canvas.targetFindTolerance = previousTolerance;
      if (annotation) {
        canvas.remove(annotation);
        erasedDuringGestureRef.current = true;
        canvas.requestRenderAll();
      }
    };

    const onMouseDown = (event) => {
      const nativeEvent = event.e;
      if (
        modeRef.current === 'select' &&
        nativeEvent.button === 0 &&
        event.target instanceof fabric.FabricImage
      ) {
        canvasImageDragOriginRef.current = {
          left: event.target.left,
          top: event.target.top
        };
      }
      if (modeRef.current === 'annotate' && annotationToolRef.current === 'eraser' && nativeEvent.button !== 1) {
        erasingGestureRef.current = true;
        erasedDuringGestureRef.current = false;
        eraseAnnotationAt(nativeEvent);
        return;
      }
      if (
        modeRef.current === 'annotate' &&
        SHAPE_TOOLS.has(annotationToolRef.current) &&
        nativeEvent.button !== 1
      ) {
        if (event.target && !event.target.__canvasHelper) return;
        const point = canvas.getScenePoint(nativeEvent);
        const commonOptions = {
          left: point.x,
          top: point.y,
          fill: shapeFillTransparentRef.current ? 'transparent' : shapeFillColorRef.current,
          stroke: shapeStrokeColorRef.current,
          strokeWidth: penSizeRef.current,
          strokeUniform: true,
          selectable: true,
          evented: true,
          name: 'Shape',
          cornerColor: '#ffffff',
          cornerStrokeColor: '#232751',
          borderColor: '#232751',
          cornerSize: 10,
          transparentCorners: false
        };

        shapeStart = point;
        if (annotationToolRef.current === 'line') {
          const lineOptions = { ...commonOptions };
          delete lineOptions.left;
          delete lineOptions.top;
          shapeObject = new fabric.Line([point.x, point.y, point.x, point.y], {
            ...lineOptions,
            fill: shapeStrokeColorRef.current
          });
        } else if (annotationToolRef.current === 'rectangle') {
          shapeObject = new fabric.Rect({ ...commonOptions, width: 1, height: 1 });
        } else if (annotationToolRef.current === 'circle') {
          shapeObject = new fabric.Ellipse({ ...commonOptions, rx: 1, ry: 1 });
        } else {
          shapeObject = new fabric.Triangle({ ...commonOptions, width: 1, height: 1 });
        }
        canvas.discardActiveObject();
        canvas.add(shapeObject);
        canvas.requestRenderAll();
        return;
      }
      if (modeRef.current === 'snip' && nativeEvent.button !== 1) {
        const point = canvas.getScenePoint(nativeEvent);
        snipStart = point;
        dragging = true;
        snipRectangle = new fabric.Rect({
          left: point.x,
          top: point.y,
          width: 0,
          height: 0,
          fill: 'rgba(16, 185, 129, 0.16)',
          stroke: '#232751',
          strokeWidth: 2 / canvas.getZoom(),
          strokeDashArray: [7, 5],
          selectable: false,
          evented: false
        });
        snipRectangle.__canvasHelper = true;
        canvas.add(snipRectangle);
        return;
      }

      if (modeRef.current === 'pan' || spaceDown || nativeEvent.button === 1) {
        dragging = true;
        lastX = nativeEvent.clientX;
        lastY = nativeEvent.clientY;
        setPanCursor('grabbing');
      }
    };

    const onMouseMove = (event) => {
      if (erasingGestureRef.current) {
        eraseAnnotationAt(event.e);
        return;
      }
      if (shapeObject && shapeStart) {
        const point = canvas.getScenePoint(event.e);
        if (shapeObject instanceof fabric.Line) {
          shapeObject.set({ x2: point.x, y2: point.y });
        } else {
          const left = Math.min(point.x, shapeStart.x);
          const top = Math.min(point.y, shapeStart.y);
          const width = Math.max(1, Math.abs(point.x - shapeStart.x));
          const height = Math.max(1, Math.abs(point.y - shapeStart.y));
          if (shapeObject instanceof fabric.Ellipse) {
            shapeObject.set({ left, top, rx: width / 2, ry: height / 2 });
          } else {
            shapeObject.set({ left, top, width, height });
          }
        }
        shapeObject.setCoords();
        canvas.requestRenderAll();
        return;
      }
      if (!dragging) return;
      const nativeEvent = event.e;
      if (modeRef.current === 'snip' && snipRectangle && snipStart) {
        const point = canvas.getScenePoint(nativeEvent);
        snipRectangle.set({
          left: Math.min(point.x, snipStart.x),
          top: Math.min(point.y, snipStart.y),
          width: Math.abs(point.x - snipStart.x),
          height: Math.abs(point.y - snipStart.y)
        });
        snipRectangle.setCoords();
        canvas.requestRenderAll();
        return;
      }

      if (modeRef.current === 'pan' || spaceDown) {
        const viewport = canvas.viewportTransform;
        viewport[4] += nativeEvent.clientX - lastX;
        viewport[5] += nativeEvent.clientY - lastY;
        lastX = nativeEvent.clientX;
        lastY = nativeEvent.clientY;
        canvas.requestRenderAll();
      }
    };

    const finishSnip = async () => {
      const rectangle = snipRectangle;
      if (!rectangle) return;
      const selection = rectangle.getBoundingRect();
      const images = canvas.getObjects().filter((object) => object instanceof fabric.FabricImage);
      const target = [...images].reverse().find((image) => image.intersectsWithObject(rectangle));

      if (target && selection.width >= MIN_CROP_SIZE && selection.height >= MIN_CROP_SIZE) {
        const targetBounds = target.getBoundingRect();
        const left = Math.max(targetBounds.left, selection.left);
        const top = Math.max(targetBounds.top, selection.top);
        const right = Math.min(targetBounds.left + targetBounds.width, selection.left + selection.width);
        const bottom = Math.min(targetBounds.top + targetBounds.height, selection.top + selection.height);

        if (right - left >= MIN_CROP_SIZE && bottom - top >= MIN_CROP_SIZE) {
          const clone = await target.clone();
          const scaleX = Math.abs(target.scaleX || 1);
          const scaleY = Math.abs(target.scaleY || 1);
          clone.set({
            cropX: (target.cropX || 0) + ((left - targetBounds.left) / scaleX),
            cropY: (target.cropY || 0) + ((top - targetBounds.top) / scaleY),
            width: (right - left) / scaleX,
            height: (bottom - top) / scaleY,
            left: left + 18,
            top: top + 18,
            angle: 0
          });
          styleImageControls(clone);
          canvas.add(clone);
          canvas.setActiveObject(clone);
          setMessage('Snip created as a new movable image.');
        }
      } else {
        setMessage('Draw the snip rectangle over an image.');
      }

      canvas.remove(rectangle);
      snipRectangle = null;
      snipStart = null;
      setMode('select');
      canvas.requestRenderAll();
      syncSelection(canvas);
    };

    const onMouseUp = () => {
      if (erasingGestureRef.current) {
        erasingGestureRef.current = false;
        if (erasedDuringGestureRef.current) {
          commitHistory(canvas);
          setMessage('Annotation erased.');
        }
        return;
      }
      if (shapeObject) {
        const completedShape = shapeObject;
        const bounds = completedShape.getBoundingRect();
        shapeObject = null;
        shapeStart = null;
        if (Math.max(bounds.width, bounds.height) < 6) {
          canvas.remove(completedShape);
          setMessage('Drag on the canvas to size the shape.');
        } else {
          completedShape.setCoords();
          canvas.setActiveObject(completedShape);
          syncSelection(canvas);
          commitHistory(canvas);
          setMessage('Shape added. Drag its handles to resize it, or keep drawing more shapes.');
        }
        canvas.requestRenderAll();
        return;
      }
      if (modeRef.current === 'snip' && snipRectangle) {
        dragging = false;
        finishSnip();
        return;
      }
      dragging = false;
      canvas.setViewportTransform(canvas.viewportTransform);
      setPanCursor(modeRef.current === 'pan' || spaceDown ? 'grab' : 'default');
      window.requestAnimationFrame(() => {
        if (draggingCanvasImageRef.current || canvasImageDragOriginRef.current) resetCanvasImageDrag();
      });
    };

    const onWheel = (event) => {
      let nextZoom = canvas.getZoom() * (0.999 ** event.e.deltaY);
      nextZoom = Math.min(4, Math.max(0.2, nextZoom));
      canvas.zoomToPoint(new fabric.Point(event.e.offsetX, event.e.offsetY), nextZoom);
      setZoom(nextZoom);
      event.e.preventDefault();
      event.e.stopPropagation();
    };

    const isEditableKeyboardTarget = (event) => {
      const target = event.target;
      const tagName = target?.tagName || document.activeElement?.tagName;
      return ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(tagName) ||
        Boolean(target?.isContentEditable);
    };

    const onKeyDown = (event) => {
      if (isEditableKeyboardTarget(event)) return;
      if (event.code === 'Space') {
        event.preventDefault();
        spaceDown = true;
        if (modeRef.current === 'annotate') canvas.isDrawingMode = false;
        canvas.selection = false;
        setPanCursor('grab');
      }
      if ((event.key === 'Delete' || event.key === 'Backspace') && modeRef.current !== 'crop') {
        const objects = canvas.getActiveObjects();
        objects.forEach((object) => canvas.remove(object));
        canvas.discardActiveObject();
        canvas.requestRenderAll();
      }
    };

    const onKeyUp = (event) => {
      if (isEditableKeyboardTarget(event)) return;
      if (event.code === 'Space') {
        spaceDown = false;
        canvas.isDrawingMode = modeRef.current === 'annotate' && annotationToolRef.current === 'pen';
        canvas.selection = modeRef.current === 'select';
        setPanCursor(modeRef.current === 'pan' ? 'grab' : modeRef.current === 'annotate' ? 'crosshair' : 'default');
      }
    };

    canvas.on('mouse:down', onMouseDown);
    canvas.on('mouse:move', onMouseMove);
    canvas.on('mouse:up', onMouseUp);
    canvas.on('mouse:wheel', onWheel);
    canvas.on('object:moving', onObjectMoving);
    canvas.on('object:modified', onObjectModified);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    return () => {
      canvas.off('mouse:down', onMouseDown);
      canvas.off('mouse:move', onMouseMove);
      canvas.off('mouse:up', onMouseUp);
      canvas.off('mouse:wheel', onWheel);
      canvas.off('object:moving', onObjectMoving);
      canvas.off('object:modified', onObjectModified);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [attachCanvasImageAsReference, commitHistory, syncSelection]);

  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    if (mode === 'crop') {
      canvas.isDrawingMode = false;
      return;
    }
    const shapeToolActive = mode === 'annotate' && SHAPE_TOOLS.has(annotationTool);
    const interactive = mode === 'select' || shapeToolActive;
    const annotationActive = mode === 'annotate';
    const annotating = annotationActive && annotationTool === 'pen';
    canvas.isDrawingMode = annotating;
    if (canvas.freeDrawingBrush) {
      canvas.freeDrawingBrush.color = annotationColor;
      canvas.freeDrawingBrush.width = penSize;
    }
    canvas.selection = interactive;
    canvas.skipTargetFind = !interactive;
    canvas.defaultCursor = mode === 'pan' ? 'grab' : (mode === 'snip' || annotationActive) ? 'crosshair' : 'default';
    canvas.hoverCursor = shapeToolActive ? 'move' : canvas.defaultCursor;
    if (!interactive) canvas.discardActiveObject();
    canvas.requestRenderAll();
  }, [annotationColor, annotationTool, mode, penSize]);

  const updateDimensions = (nextWidth, nextHeight) => {
    const canvas = fabricCanvasRef.current;
    const object = canvas?.getActiveObject();
    if (!canvas || !object || object.__canvasHelper) return;
    const width = Math.max(1, Number(nextWidth) || 1);
    const height = Math.max(1, Number(nextHeight) || 1);
    object.set({
      scaleX: width / (object.width || 1),
      scaleY: height / (object.height || 1)
    });
    object.setCoords();
    canvas.requestRenderAll();
    setDimensions({ width, height });
    scheduleHistoryCommit(canvas);
  };

  const updateRotation = (value) => {
    const nextRotation = Number(value) || 0;
    const canvas = fabricCanvasRef.current;
    const object = canvas?.getActiveObject();
    setRotation(nextRotation);
    if (canvas && object && !object.__canvasHelper) {
      object.set({ angle: nextRotation });
      object.setCoords();
      canvas.requestRenderAll();
      scheduleHistoryCommit(canvas);
    }
  };

  const updateDrawingStrokeColor = (color) => {
    setAnnotationColor(color);
    const canvas = fabricCanvasRef.current;
    const object = canvas?.getActiveObject();
    if (canvas && object?.name === 'Shape') {
      object.set({ stroke: color, ...(object instanceof fabric.Line ? { fill: color } : {}) });
      object.setCoords();
      canvas.requestRenderAll();
      scheduleHistoryCommit(canvas);
    }
  };

  const updateDrawingStrokeWidth = (width) => {
    setPenSize(width);
    const canvas = fabricCanvasRef.current;
    const object = canvas?.getActiveObject();
    if (canvas && object?.name === 'Shape') {
      object.set({ strokeWidth: width });
      object.setCoords();
      canvas.requestRenderAll();
      syncSelection(canvas);
      scheduleHistoryCommit(canvas);
    }
  };

  const updateDrawingFillColor = (color) => {
    setShapeFillColor(color);
    setShapeFillTransparent(false);
    const canvas = fabricCanvasRef.current;
    const object = canvas?.getActiveObject();
    if (canvas && object?.name === 'Shape' && !(object instanceof fabric.Line)) {
      object.set({ fill: color });
      canvas.requestRenderAll();
      scheduleHistoryCommit(canvas);
    }
  };

  const toggleTransparentFill = () => {
    const transparent = !shapeFillTransparent;
    setShapeFillTransparent(transparent);
    const canvas = fabricCanvasRef.current;
    const object = canvas?.getActiveObject();
    if (canvas && object?.name === 'Shape' && !(object instanceof fabric.Line)) {
      object.set({ fill: transparent ? 'transparent' : shapeFillColor });
      canvas.requestRenderAll();
      scheduleHistoryCommit(canvas);
    }
  };

  const removeSelectedBackground = async () => {
    const canvas = fabricCanvasRef.current;
    const image = canvas?.getActiveObject();
    if (!canvas || !(image instanceof fabric.FabricImage)) {
      setMessage('Select an image before removing its background.');
      return;
    }
    if (image.__backgroundRemovalOriginalElement) {
      setMessage('This image already has its background removed. Restore it before processing again.');
      return;
    }

    const originalElement = image.getElement();
    const source = image.getSrc();
    if (!source) {
      setMessage('The selected image source is unavailable. Try uploading it again.');
      return;
    }

    setIsRemovingBackground(true);
    setBackgroundRemovalProgress(0);
    setMessage('Preparing high-quality subject detection… The first run downloads the AI model.');

    try {
      const { removeBackground } = await import('@imgly/background-removal');
      const result = await removeBackground(source, {
        model: 'isnet',
        device: 'cpu',
        output: { format: 'image/png', quality: 1 },
        progress: (key, current, total) => {
          const percent = total > 0 ? Math.round((current / total) * 100) : 0;
          setBackgroundRemovalProgress(percent);
          if (key.startsWith('fetch:')) {
            setMessage(`Downloading the background-removal model… ${percent}%`);
          } else if (key.startsWith('compute:')) {
            setMessage(`Separating the subject and refining edges… ${percent}%`);
          }
        }
      });

      const resultUrl = URL.createObjectURL(result);
      let processedElement;
      try {
        processedElement = await fabric.util.loadImage(resultUrl, { crossOrigin: 'anonymous' });
      } catch (error) {
        URL.revokeObjectURL(resultUrl);
        throw error;
      }

      if (!canvas.getObjects().includes(image)) {
        URL.revokeObjectURL(resultUrl);
        return;
      }

      image.__backgroundRemovalOriginalElement = originalElement;
      image.__backgroundRemovalResultUrl = resultUrl;
      image.setElement(processedElement, { width: image.width, height: image.height });
      image.setCoords();
      canvas.setActiveObject(image);
      canvas.requestRenderAll();
      syncSelection(canvas);
      scheduleHistoryCommit(canvas);
      setBackgroundRemovalProgress(100);
      setMessage('Background removed with full-resolution edge matting. Export as PNG to keep transparency.');
      setExportFormat('png');
    } catch (error) {
      console.error('Unable to remove image background:', error);
      setMessage('Background removal failed. Check your connection and try again; the image was not changed.');
    } finally {
      setIsRemovingBackground(false);
    }
  };

  const restoreSelectedBackground = () => {
    const canvas = fabricCanvasRef.current;
    const image = canvas?.getActiveObject();
    if (!canvas || !(image instanceof fabric.FabricImage) || !image.__backgroundRemovalOriginalElement) return;

    image.setElement(image.__backgroundRemovalOriginalElement, { width: image.width, height: image.height });
    if (image.__backgroundRemovalResultUrl) URL.revokeObjectURL(image.__backgroundRemovalResultUrl);
    delete image.__backgroundRemovalOriginalElement;
    delete image.__backgroundRemovalResultUrl;
    image.setCoords();
    canvas.requestRenderAll();
    syncSelection(canvas);
    scheduleHistoryCommit(canvas);
    setMessage('Original background restored.');
  };

  const saveProject = async () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || isSavingProject) return;

    setIsSavingProject(true);
    setMessage('Preparing the complete canvas project for Cloudflare R2...');
    try {
      const snapshot = await embedCanvasImageSources(JSON.parse(serializeCanvas(canvas)));
      const result = await saveCanvasProjectToR2({
        title: projectTitle.trim() || 'Untitled Canvas',
        canvas: snapshot,
        objectKey: currentProjectKey
      });
      setCurrentProjectKey(result.objectKey);
      setProjectTitle(result.title);
      setSavedProjects((current) => [
        { objectKey: result.objectKey, title: result.title, savedAt: result.savedAt },
        ...current.filter(({ objectKey }) => objectKey !== result.objectKey)
      ]);
      setMessage(`Project “${result.title}” saved to Cloudflare R2.`);
    } catch (error) {
      console.error('Unable to save the canvas project to R2:', error);
      setMessage(error?.message || 'The canvas project could not be saved to Cloudflare R2.');
    } finally {
      setIsSavingProject(false);
    }
  };

  const openSavedProject = async (projectSummary) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || loadingProjectKey) return;
    setLoadingProjectKey(projectSummary.objectKey);
    setMessage(`Loading “${projectSummary.title}”...`);
    try {
      const project = await loadCanvasProjectFromR2(projectSummary.objectKey);
      await canvas.loadFromJSON(project.canvas);
      canvas.getObjects().forEach((object) => {
        if (object instanceof fabric.FabricImage) styleImageControls(object);
      });
      canvas.discardActiveObject();
      canvas.requestRenderAll();
      setSelectedObject(null);
      setMode('select');
      setProjectTitle(project.title || projectSummary.title || 'Untitled Canvas');
      setCurrentProjectKey(projectSummary.objectKey);
      historyRef.current = [];
      historyIndexRef.current = -1;
      commitHistory(canvas);
      setMessage(`Project “${project.title || projectSummary.title}” loaded.`);
      setShowSavedProjects(false);
    } catch (error) {
      console.error('Unable to load the canvas project:', error);
      setMessage(error?.message || 'The canvas project could not be loaded.');
    } finally {
      setLoadingProjectKey(null);
    }
  };

  const duplicateSelected = async () => {
    const canvas = fabricCanvasRef.current;
    const active = canvas?.getActiveObject();
    if (!canvas || !active || active.__canvasHelper) return;

    if (active instanceof fabric.ActiveSelection) {
      const objects = active.getObjects();
      canvas.discardActiveObject();
      const clones = await Promise.all(objects.map((object) => object.clone()));
      clones.forEach((clone) => {
        clone.set({ left: (clone.left || 0) + 24, top: (clone.top || 0) + 24 });
        styleImageControls(clone);
        clone.setCoords();
      });
      canvas.add(...clones);
      const selection = new fabric.ActiveSelection(clones, { canvas });
      canvas.setActiveObject(selection);
      selection.setCoords();
      canvas.requestRenderAll();
      syncSelection(canvas);
      commitHistory(canvas);
      setMessage(`${clones.length} selected items duplicated.`);
      return;
    }

    const clone = await active.clone();
    clone.set({ left: (clone.left || 0) + 24, top: (clone.top || 0) + 24 });
    styleImageControls(clone);
    canvas.add(clone);
    canvas.setActiveObject(clone);
    canvas.requestRenderAll();
    syncSelection(canvas);
    commitHistory(canvas);
  };

  const groupSelected = () => {
    const canvas = fabricCanvasRef.current;
    const selection = canvas?.getActiveObject();
    if (!canvas || !(selection instanceof fabric.ActiveSelection) || selection.size() < 2) return;

    const objects = selection.getObjects();
    const objectIndexes = objects
      .map((object) => canvas.getObjects().indexOf(object))
      .filter((index) => index >= 0);
    const insertAt = objectIndexes.length ? Math.min(...objectIndexes) : canvas.getObjects().length;

    canvas.discardActiveObject();
    canvas.remove(...objects);
    const group = new fabric.Group(objects, { name: 'Group' });
    styleImageControls(group);
    canvas.insertAt(insertAt, group);
    canvas.setActiveObject(group);
    group.setCoords();
    canvas.requestRenderAll();
    syncSelection(canvas);
    commitHistory(canvas);
    setMessage(`${objects.length} items grouped. Move, resize, or rotate them together.`);
  };

  const ungroupSelected = () => {
    const canvas = fabricCanvasRef.current;
    const group = canvas?.getActiveObject();
    if (!canvas || !(group instanceof fabric.Group) || group instanceof fabric.ActiveSelection) return;

    const groupIndex = canvas.getObjects().indexOf(group);
    canvas.discardActiveObject();
    const objects = group.removeAll();
    canvas.remove(group);
    canvas.insertAt(Math.max(0, groupIndex), ...objects);
    objects.forEach((object) => {
      if (object instanceof fabric.FabricImage) styleImageControls(object);
      object.setCoords();
    });

    const selection = new fabric.ActiveSelection(objects, { canvas });
    canvas.setActiveObject(selection);
    selection.setCoords();
    canvas.requestRenderAll();
    syncSelection(canvas);
    commitHistory(canvas);
    setMessage(`${objects.length} items ungrouped and selected.`);
  };

  const deleteSelected = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    canvas.getActiveObjects().forEach((object) => {
      if (object.__backgroundRemovalResultUrl) URL.revokeObjectURL(object.__backgroundRemovalResultUrl);
      canvas.remove(object);
    });
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    setSelectedObject(null);
  };

  const changeZoom = (nextZoom) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const limitedZoom = Math.min(4, Math.max(0.2, nextZoom));
    canvas.zoomToPoint(
      new fabric.Point((canvas.width || 0) / 2, (canvas.height || 0) / 2),
      limitedZoom
    );
    setZoom(limitedZoom);
  };

  const resetZoom = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    setZoom(1);
  };

  const exportCanvas = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const objects = canvas.getObjects().filter((object) => !object.__canvasHelper);
    if (!objects.length) {
      setMessage('Add an image before exporting.');
      return;
    }

    const previousBackground = canvas.backgroundColor;
    const previousViewport = [...canvas.viewportTransform];
    const activeObjects = canvas.getActiveObjects();
    canvas.discardActiveObject();
    canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    if (exportFormat === 'jpeg') canvas.backgroundColor = '#ffffff';
    canvas.requestRenderAll();

    // Export the complete composition in scene coordinates. Objects can extend
    // beyond the initial canvas after moving, scaling, or rotation; clamping
    // these bounds to the editor dimensions cuts those visible pixels off.
    const exportBounds = getExportBounds(objects);
    const dataUrl = canvas.toDataURL({
      format: exportFormat,
      quality: 0.94,
      multiplier: 2,
      ...exportBounds
    });

    canvas.backgroundColor = previousBackground;
    canvas.setViewportTransform(previousViewport);
    if (activeObjects.length === 1) canvas.setActiveObject(activeObjects[0]);
    canvas.requestRenderAll();

    const link = document.createElement('a');
    link.download = `parrotdocs-canvas.${exportFormat === 'jpeg' ? 'jpg' : 'png'}`;
    link.href = dataUrl;
    link.click();
    setMessage(`${exportFormat.toUpperCase()} exported at 2× resolution.`);
  };

  const activateTool = (nextMode) => {
    if (mode === 'crop') cancelCrop();
    setMode(nextMode);
    if (nextMode === 'snip') setMessage('Drag over part of an image to create a new snipped image.');
    if (nextMode === 'annotate') setMessage('Choose a pen or shape from the drawing strip, then drag on the canvas.');
  };

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if (editorRef.current?.requestFullscreen) {
        await editorRef.current.requestFullscreen();
      } else {
        setMessage('Fullscreen is not supported by this browser.');
      }
    } catch (error) {
      console.error('Unable to change fullscreen mode:', error);
      setMessage('The browser could not open Canvas in fullscreen.');
    }
  };

  const startDrawToolbarDrag = (event) => {
    if (event.button !== 0) return;
    const wrapper = canvasWrapperRef.current;
    const toolbar = drawToolbarRef.current;
    if (!wrapper || !toolbar) return;
    const wrapperBounds = wrapper.getBoundingClientRect();
    const toolbarBounds = toolbar.getBoundingClientRect();
    drawToolbarDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: toolbarBounds.left - wrapperBounds.left,
      originY: toolbarBounds.top - wrapperBounds.top
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const moveDrawToolbar = (event) => {
    const drag = drawToolbarDragRef.current;
    const wrapper = canvasWrapperRef.current;
    const toolbar = drawToolbarRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !wrapper || !toolbar) return;
    setDrawToolbarPosition({
      x: Math.max(0, Math.min(wrapper.clientWidth - toolbar.offsetWidth, drag.originX + event.clientX - drag.startX)),
      y: Math.max(0, Math.min(wrapper.clientHeight - toolbar.offsetHeight, drag.originY + event.clientY - drag.startY))
    });
  };

  const finishDrawToolbarDrag = (event) => {
    const drag = drawToolbarDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    drawToolbarDragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const startAiLauncherDrag = (event) => {
    if (event.button !== 0) return;
    const wrapper = canvasWrapperRef.current;
    if (!wrapper) return;
    const wrapperBounds = wrapper.getBoundingClientRect();
    const launcherBounds = event.currentTarget.getBoundingClientRect();
    aiLauncherDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: launcherBounds.left - wrapperBounds.left,
      originY: launcherBounds.top - wrapperBounds.top,
      moved: false
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveAiLauncher = (event) => {
    const drag = aiLauncherDragRef.current;
    const wrapper = canvasWrapperRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !wrapper) return;
    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) drag.moved = true;
    setAiLauncherPosition({
      x: Math.max(0, Math.min(wrapper.clientWidth - 56, drag.originX + deltaX)),
      y: Math.max(0, Math.min(wrapper.clientHeight - 56, drag.originY + deltaY))
    });
  };

  const finishAiLauncherDrag = (event) => {
    const drag = aiLauncherDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    suppressAiLauncherClickRef.current = drag.moved;
    aiLauncherDragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const toggleAiPrompt = () => {
    if (suppressAiLauncherClickRef.current) {
      suppressAiLauncherClickRef.current = false;
      return;
    }
    if (!isGeneratingImage) {
      if (isAiPromptOpen) clearAiRegion();
      setIsAiPromptOpen((open) => !open);
    }
  };

  const aiLauncherOnRight = aiLauncherPosition &&
    aiLauncherPosition.x > (canvasWrapperRef.current?.clientWidth || 0) / 2;
  const aiLauncherNearTop = aiLauncherPosition && aiLauncherPosition.y < 300;

  return (
    <div
      ref={editorRef}
      className={`flex flex-col overflow-hidden bg-slate-950 shadow-2xl ${isFullscreen ? 'h-screen w-screen rounded-none border-0' : 'min-h-[680px] rounded-2xl border border-slate-800'}`}
    >
      <input
        ref={fileInputRef}
        className="hidden"
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={(event) => {
          importFile(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
      <input
        ref={aiReferenceInputRef}
        className="hidden"
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={(event) => {
          selectAiReferenceImage(event.target.files?.[0]);
          event.target.value = '';
        }}
      />

      <div className="relative z-40 flex min-h-16 flex-wrap items-center justify-between gap-3 border-b border-slate-800 bg-slate-900 px-4 py-3">
        <div className="relative flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowSavedProjects((visible) => !visible)}
            className={`rounded-lg p-2 transition-colors ${showSavedProjects ? 'bg-[#232751] text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white'}`}
            title="Open saved projects"
            aria-label="Open saved projects"
            aria-expanded={showSavedProjects}
          >
            <FolderOpen className="h-4 w-4" />
          </button>
          {showSavedProjects && (
            <div className="absolute left-0 top-full z-50 mt-3 w-72 rounded-xl border border-slate-700 bg-slate-900 p-3 shadow-2xl">
              <div className="mb-2 flex items-center justify-between gap-3 px-1">
                <span className="text-[11px] font-black uppercase tracking-widest text-slate-400">Saved projects</span>
                <button type="button" onClick={() => setShowSavedProjects(false)} className="rounded-md p-1 text-slate-500 hover:bg-slate-800 hover:text-white" title="Close saved projects">
                  <X className="h-4 w-4" />
                </button>
              </div>
              {savedProjects.length > 0 ? (
                <div className="max-h-72 space-y-2 overflow-y-auto">
                  {savedProjects.map((project) => (
                    <button
                      key={project.objectKey}
                      type="button"
                      onClick={() => openSavedProject(project)}
                      disabled={Boolean(loadingProjectKey)}
                      className={`flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-xs transition-colors ${currentProjectKey === project.objectKey ? 'border-[#4f579e] bg-[#232751] text-white' : 'border-slate-700 bg-slate-950 text-slate-300 hover:border-slate-500 hover:bg-slate-800'} disabled:cursor-wait`}
                      title={`Load ${project.title}`}
                    >
                      {loadingProjectKey === project.objectKey
                        ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                        : <FolderOpen className="h-4 w-4 shrink-0" />}
                      <span className="min-w-0 flex-1 truncate font-bold">{project.title}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="rounded-lg border border-dashed border-slate-700 px-3 py-6 text-center text-xs text-slate-500">Saved canvases will appear here.</p>
              )}
            </div>
          )}
          <input
            type="text"
            value={projectTitle}
            onChange={(event) => setProjectTitle(event.target.value)}
            onBlur={() => setProjectTitle((title) => title.trim() || 'Untitled Canvas')}
            className="w-48 rounded-lg border border-transparent bg-transparent px-2 py-2 text-sm font-bold text-white outline-none transition-colors hover:border-slate-700 focus:border-slate-600 focus:bg-slate-800 sm:w-64"
            aria-label="Project title"
            title="Edit project title"
          />
          <button
            type="button"
            onClick={saveProject}
            disabled={isSavingProject}
            className="flex items-center gap-2 rounded-lg bg-[#232751] px-3 py-2 text-xs font-bold text-white hover:bg-[#1b1f43] disabled:cursor-not-allowed disabled:bg-slate-800 disabled:text-slate-500"
            title="Save the complete canvas project to Cloudflare R2"
          >
            {isSavingProject
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <CloudUpload className="h-4 w-4" />}
            {isSavingProject ? 'Saving...' : 'Save'}
          </button>
          <span className="mx-1 hidden h-7 w-px bg-slate-700 lg:block" />
          <button
            type="button"
            onClick={() => activateTool(mode === 'pan' ? 'select' : 'pan')}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold ${mode === 'pan' ? 'bg-[#232751] text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
          >
            <Hand className="h-4 w-4" /> Pan
          </button>
          <button
            type="button"
            onClick={() => activateTool(mode === 'annotate' ? 'select' : 'annotate')}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold ${mode === 'annotate' ? 'bg-[#232751] text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
            aria-pressed={mode === 'annotate'}
            aria-label="Draw"
            title="Open drawing tools"
          >
            <Pencil className="h-4 w-4" /> Draw
          </button>
          <span className="mx-1 hidden h-7 w-px bg-slate-700 lg:block" />
          <button
            type="button"
            onClick={() => setShowAssets((visible) => !visible)}
            className="flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-xs font-bold text-slate-300 hover:bg-slate-700"
          >
            <ImageIcon className="h-4 w-4" /> Assets
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {mode === 'crop' && (
            <>
              <button type="button" onClick={applyCrop} className="flex items-center gap-1 rounded-lg bg-[#232751] px-3 py-2 text-xs font-bold text-white hover:bg-[#1b1f43]">
                <Check className="h-4 w-4" /> Apply crop
              </button>
              <button type="button" onClick={cancelCrop} className="rounded-lg bg-slate-800 p-2 text-slate-300 hover:bg-slate-700" title="Cancel crop">
                <X className="h-4 w-4" />
              </button>
            </>
          )}
          <select
            value={exportFormat}
            onChange={(event) => setExportFormat(event.target.value)}
            className="rounded-lg border border-slate-700 bg-slate-800 px-2 py-2 text-xs font-bold text-white"
            aria-label="Export format"
          >
            <option value="png">PNG</option>
            <option value="jpeg">JPG</option>
          </select>
          <button
            type="button"
            onClick={() => {
              setShowTransparency((visible) => {
                const nextVisible = !visible;
                setMessage(nextVisible
                  ? 'Transparency preview enabled. The checkerboard shows transparent areas.'
                  : 'White canvas preview enabled. PNG exports still preserve transparency.');
                return nextVisible;
              });
            }}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold ${showTransparency ? 'bg-[#232751] text-white' : 'bg-white text-slate-700 hover:bg-slate-100'}`}
            title={showTransparency ? 'Preview on a white canvas' : 'Show transparent areas with a checkerboard'}
            aria-pressed={showTransparency}
          >
            <Square className="h-4 w-4" />
            <span className="hidden sm:inline">{showTransparency ? 'White preview' : 'Transparency'}</span>
          </button>
          <button
            type="button"
            onClick={toggleFullscreen}
            className="rounded-lg bg-slate-800 p-2 text-slate-300 hover:bg-slate-700 hover:text-white"
            title={isFullscreen ? 'Exit fullscreen' : 'Open Canvas fullscreen'}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Open Canvas fullscreen'}
          >
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <button type="button" onClick={exportCanvas} className="flex items-center gap-2 rounded-xl bg-[#232751] px-4 py-2 text-xs font-black uppercase tracking-widest text-white hover:bg-[#1b1f43]">
            <Download className="h-4 w-4" /> Export
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {showAssets && (
          <aside className="hidden w-64 shrink-0 overflow-y-auto border-r border-slate-800 bg-slate-900 p-4 md:block">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
                <ImageIcon className="h-4 w-4" /> Uploaded assets
              </h3>
              <div className="flex items-center gap-2">
                {isLoadingR2Assets && <Loader2 className="h-4 w-4 animate-spin text-slate-500" aria-label="Loading saved images" />}
                <button type="button" onClick={() => fileInputRef.current?.click()} className="rounded-md bg-slate-800 p-1.5 text-slate-400 hover:text-white" title="Upload another image"><Plus className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {assets.map((asset) => (
                <div key={asset.id} className="group relative aspect-square overflow-hidden rounded-xl border border-slate-700 bg-slate-950 hover:border-[#232751]">
                  <button type="button" onClick={() => addImageToCanvas(asset.url, asset.name)} className="absolute inset-0" title={`Add ${asset.name} to canvas`}>
                    <img src={asset.url} alt={asset.name} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                    <span className="absolute inset-0 flex items-center justify-center bg-[#232751]/20 opacity-0 transition-opacity group-hover:opacity-100"><Plus className="h-6 w-6 text-white" /></span>
                  </button>
                  <button
                    type="button"
                    onClick={() => removeAsset(asset)}
                    disabled={Boolean(deletingAssetId)}
                    className="absolute right-1.5 top-1.5 z-10 rounded-md bg-slate-950/85 p-1.5 text-slate-300 opacity-0 shadow-lg backdrop-blur transition-opacity hover:bg-red-600 hover:text-white group-hover:opacity-100 focus:opacity-100 disabled:cursor-wait"
                    title={`Remove ${asset.name} from Assets`}
                    aria-label={`Remove ${asset.name} from Assets`}
                  >
                    {deletingAssetId === asset.id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Trash2 className="h-3.5 w-3.5" />}
                  </button>
                </div>
              ))}
            </div>
            {!assets.length && (
              <button type="button" onClick={() => fileInputRef.current?.click()} className="flex w-full flex-col items-center gap-3 rounded-xl border border-dashed border-slate-700 px-3 py-10 text-center text-xs text-slate-500 hover:border-slate-500 hover:text-slate-300">
                <Upload className="h-7 w-7" />
                Upload PNG, JPG, WebP, or paste an image
              </button>
            )}
          </aside>
        )}

        <main
          className="relative flex min-w-0 flex-1 flex-col bg-slate-950 p-3"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            importFile(event.dataTransfer.files?.[0]);
          }}
        >
          <div
            ref={canvasWrapperRef}
            className="relative min-h-[540px] flex-1 overflow-hidden rounded-xl border border-slate-800 shadow-inner"
            style={showTransparency ? {
              backgroundColor: '#e2e8f0',
              backgroundImage: 'linear-gradient(45deg, #cbd5e1 25%, transparent 25%), linear-gradient(-45deg, #cbd5e1 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #cbd5e1 75%), linear-gradient(-45deg, transparent 75%, #cbd5e1 75%)',
              backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
              backgroundSize: '20px 20px'
            } : { backgroundColor: '#ffffff' }}
          >
            <canvas ref={canvasElementRef} />
            {selectedObject && mode !== 'crop' && (
              <div className="absolute left-1/2 top-4 z-30 flex max-w-[calc(100%-2rem)] -translate-x-1/2 items-center gap-2 overflow-x-auto rounded-2xl border border-slate-700 bg-slate-950/95 p-2 shadow-2xl backdrop-blur">
                {selectedObject instanceof fabric.FabricImage && (
                  <>
                    <button
                      type="button"
                      onClick={() => activateTool('snip')}
                      className="flex shrink-0 items-center gap-1.5 rounded-lg bg-slate-800 px-2.5 py-2 text-xs font-bold text-slate-300 hover:bg-slate-700 hover:text-white"
                      title="Snip part of an image"
                    >
                      <Scissors className="h-4 w-4" /> Snip
                    </button>
                    <button
                      type="button"
                      onClick={startCrop}
                      className="flex shrink-0 items-center gap-1.5 rounded-lg bg-slate-800 px-2.5 py-2 text-xs font-bold text-slate-300 hover:bg-slate-700 hover:text-white"
                      title="Crop the selected image"
                    >
                      <Crop className="h-4 w-4" /> Crop
                    </button>
                    <button
                      type="button"
                      onClick={removeSelectedBackground}
                      disabled={isRemovingBackground || Boolean(selectedObject.__backgroundRemovalOriginalElement)}
                      className="flex shrink-0 items-center gap-1.5 rounded-lg bg-slate-800 px-2.5 py-2 text-xs font-bold text-slate-300 hover:bg-slate-700 hover:text-white disabled:cursor-not-allowed disabled:text-slate-600"
                      title="Remove the selected image background"
                    >
                      <Sparkles className={`h-4 w-4 ${isRemovingBackground ? 'animate-pulse' : ''}`} />
                      {isRemovingBackground ? `${backgroundRemovalProgress}%` : 'Remove BG'}
                    </button>
                    {selectedObject.__backgroundRemovalOriginalElement && (
                      <button
                        type="button"
                        onClick={restoreSelectedBackground}
                        className="flex shrink-0 items-center gap-1.5 rounded-lg bg-slate-800 px-2.5 py-2 text-xs font-bold text-slate-300 hover:bg-slate-700 hover:text-white"
                        title="Restore the original image background"
                      >
                        <RotateCcw className="h-4 w-4" /> Restore BG
                      </button>
                    )}
                  </>
                )}
                {!(selectedObject instanceof fabric.Line) && (
                  <>
                    <span className="h-7 w-px shrink-0 bg-slate-700" />
                    <label className="flex shrink-0 items-center gap-1.5 text-[10px] font-bold uppercase text-slate-500">
                      W
                      <input className="w-20 rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-sm font-bold text-white" type="number" min="1" value={dimensions.width} onChange={(event) => updateDimensions(event.target.value, dimensions.height)} />
                    </label>
                    <label className="flex shrink-0 items-center gap-1.5 text-[10px] font-bold uppercase text-slate-500">
                      H
                      <input className="w-20 rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-sm font-bold text-white" type="number" min="1" value={dimensions.height} onChange={(event) => updateDimensions(dimensions.width, event.target.value)} />
                    </label>
                  </>
                )}
                <label className="flex shrink-0 items-center gap-1.5 text-[10px] font-bold uppercase text-slate-500">
                  R
                  <input className="w-20 rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-sm font-bold text-white" type="number" value={rotation} onChange={(event) => updateRotation(event.target.value)} />
                </label>
                {selectedObject instanceof fabric.ActiveSelection && selectedObject.size() > 1 && (
                  <button type="button" onClick={groupSelected} className="flex shrink-0 items-center gap-1.5 rounded-lg bg-slate-800 px-2.5 py-2 text-xs font-bold text-slate-300 hover:bg-slate-700 hover:text-white" title="Group selected items">
                    <GroupIcon className="h-4 w-4" /> Group
                  </button>
                )}
                {selectedObject instanceof fabric.Group && !(selectedObject instanceof fabric.ActiveSelection) && (
                  <button type="button" onClick={ungroupSelected} className="flex shrink-0 items-center gap-1.5 rounded-lg bg-slate-800 px-2.5 py-2 text-xs font-bold text-slate-300 hover:bg-slate-700 hover:text-white" title="Ungroup selected items">
                    <Ungroup className="h-4 w-4" /> Ungroup
                  </button>
                )}
                <span className="h-7 w-px shrink-0 bg-slate-700" />
                <button type="button" onClick={duplicateSelected} className="shrink-0 rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white" title="Duplicate" aria-label="Duplicate selection"><Copy className="h-5 w-5" /></button>
                <button type="button" onClick={deleteSelected} className="shrink-0 rounded-lg p-2 text-slate-400 hover:bg-red-950 hover:text-red-400" title="Delete" aria-label="Delete selection"><Trash2 className="h-5 w-5" /></button>
              </div>
            )}
            <div
              className="absolute z-30"
              style={aiLauncherPosition
                ? { left: aiLauncherPosition.x, top: aiLauncherPosition.y }
                : { bottom: 16, left: 16 }}
            >
              {isDraggingCanvasImage && !isAiPromptOpen && (
                <span className={`pointer-events-none absolute whitespace-nowrap rounded-lg px-2.5 py-1.5 text-[10px] font-black shadow-xl ${aiLauncherNearTop ? 'top-16' : 'bottom-16'} ${aiLauncherOnRight ? 'right-0' : 'left-0'} ${isCanvasImageOverAi ? 'bg-amber-300 text-[#232751]' : 'bg-slate-900 text-white'}`}>
                  {isCanvasImageOverAi ? 'Release to attach' : 'Drag image here as reference'}
                </span>
              )}
              {isAiPromptOpen && (
                <form
                  onSubmit={generateImageWithAi}
                  className={`absolute max-h-[calc(100vh-8rem)] w-[22rem] overflow-y-auto rounded-2xl border border-slate-700 bg-slate-900/95 p-4 text-white shadow-2xl backdrop-blur sm:w-[26rem] ${aiLauncherNearTop ? 'top-16' : 'bottom-16'} ${aiLauncherOnRight ? 'right-0' : 'left-0'}`}
                >
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                      <p className="flex items-center gap-1.5 text-sm font-black">
                        <Sparkles className="h-4 w-4 text-amber-300" /> AI Design Studio
                      </p>
                      <p className="mt-1 text-[11px] leading-relaxed text-slate-400">
                        Generate, retouch, fill, or expand canvas artwork.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        clearAiRegion();
                        setIsAiPromptOpen(false);
                      }}
                      disabled={isGeneratingImage}
                      className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white disabled:opacity-50"
                      aria-label="Close AI image prompt"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mb-3 flex flex-wrap gap-1.5" role="tablist" aria-label="AI design tool">
                    {AI_DESIGN_MODES.map(({ id, label }) => (
                      <button
                        key={id}
                        type="button"
                        role="tab"
                        aria-selected={aiDesignMode === id}
                        onClick={() => selectAiDesignMode(id)}
                        disabled={isGeneratingImage}
                        className={`rounded-lg border px-2 py-1.5 text-[10px] font-black transition-colors ${aiDesignMode === id ? 'border-amber-300 bg-amber-300/10 text-amber-200' : 'border-slate-700 bg-slate-950 text-slate-400 hover:border-slate-500 hover:text-white'}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {aiDesignMode !== 'generate' && (
                    <div className={`mb-3 rounded-lg border px-2.5 py-2 text-[10px] font-semibold ${aiTargetRef.current ? 'border-emerald-800 bg-emerald-950/40 text-emerald-300' : 'border-amber-800 bg-amber-950/40 text-amber-200'}`}>
                      {aiTargetRef.current
                        ? `${aiTargetRef.current.name || 'Selected image'} is ready for ${AI_DESIGN_MODES.find(({ id }) => id === aiDesignMode)?.label.toLowerCase()}.`
                        : 'Select an image on the canvas, then click this tool again.'}
                    </div>
                  )}
                  <textarea
                    value={aiPrompt}
                    onChange={(event) => setAiPrompt(event.target.value)}
                    maxLength={4000}
                    rows={4}
                    autoFocus
                    disabled={isGeneratingImage}
                    placeholder={{
                      generate: 'A premium editorial product photograph with soft studio lighting...',
                      edit: 'Change the background to warm travertine; preserve the product...',
                      remove: 'Remove the flower and reconstruct the hand naturally...',
                      fill: 'Replace this area with a brushed gold label...',
                      expand: 'Continue the studio backdrop with soft shadows and negative space...'
                    }[aiDesignMode]}
                    className="w-full resize-none rounded-xl border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-white outline-none placeholder:text-slate-600 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/20 disabled:opacity-60"
                    aria-label="AI image prompt"
                  />
                  <div
                    ref={aiReferenceDropZoneRef}
                    className={`mt-3 rounded-xl transition-all ${isDraggingCanvasImage ? 'ring-2 ring-amber-300/80' : ''} ${isCanvasImageOverAi ? 'bg-amber-300/10 ring-amber-300' : ''}`}
                  >
                    {aiReferenceImage ? (
                      <div className={`flex items-center gap-2 rounded-xl border bg-slate-950 p-2 ${isCanvasImageOverAi ? 'border-amber-300' : 'border-slate-700'}`}>
                        <img
                          src={aiReferenceImage.dataUrl}
                          alt="AI reference"
                          className="h-12 w-12 rounded-lg object-cover"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-bold text-slate-200">{aiReferenceImage.name}</p>
                          <p className={`text-[10px] ${isDraggingCanvasImage ? 'text-amber-200' : 'text-slate-500'}`}>
                            {isDraggingCanvasImage ? 'Drop to replace reference' : 'Reference image attached'}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setAiReferenceImage(null)}
                          disabled={isGeneratingImage}
                          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white disabled:opacity-50"
                          aria-label="Remove reference image"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => aiReferenceInputRef.current?.click()}
                        disabled={isGeneratingImage}
                        className={`flex w-full items-center justify-center gap-2 rounded-xl border border-dashed bg-slate-950 px-3 py-2.5 text-xs font-bold hover:text-white disabled:opacity-50 ${isCanvasImageOverAi ? 'border-amber-300 text-amber-200' : 'border-slate-700 text-slate-400 hover:border-slate-500'}`}
                      >
                        <ImageIcon className="h-4 w-4" />
                        {isDraggingCanvasImage ? (isCanvasImageOverAi ? 'Release to attach' : 'Drop canvas image here') : 'Add optional style reference'}
                      </button>
                    )}
                  </div>
                  <fieldset className="mt-3" disabled={isGeneratingImage}>
                    <legend className="mb-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                      Image size
                    </legend>
                    <div className="grid grid-cols-4 gap-1.5">
                      {AI_IMAGE_SIZES.map(({ ratio, size }) => (
                        <button
                          key={ratio}
                          type="button"
                          onClick={() => setAiImageSize(size)}
                          className={`rounded-lg border px-1.5 py-2 text-[10px] font-black transition-colors ${aiImageSize === size ? 'border-amber-300 bg-amber-300/10 text-amber-200' : 'border-slate-700 bg-slate-950 text-slate-400 hover:border-slate-500 hover:text-white'}`}
                          title={`${ratio} (${size.replace('x', ' × ')})`}
                          aria-pressed={aiImageSize === size}
                        >
                          {ratio}
                        </button>
                      ))}
                    </div>
                  </fieldset>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                      Model
                      <select
                        value={aiImageModel}
                        onChange={(event) => setAiImageModel(event.target.value)}
                        disabled={isGeneratingImage}
                        className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-2 text-[11px] font-semibold normal-case tracking-normal text-white outline-none focus:border-indigo-400"
                      >
                        {AI_IMAGE_MODELS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                      </select>
                    </label>
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                      Quality
                      <select
                        value={aiImageQuality}
                        onChange={(event) => setAiImageQuality(event.target.value)}
                        disabled={isGeneratingImage}
                        className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-2 text-[11px] font-semibold capitalize normal-case tracking-normal text-white outline-none focus:border-indigo-400"
                      >
                        {AI_IMAGE_QUALITIES.map((quality) => <option key={quality} value={quality}>{quality}</option>)}
                      </select>
                    </label>
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                      Background
                      <select
                        value={aiImageBackground}
                        onChange={(event) => setAiImageBackground(event.target.value)}
                        disabled={isGeneratingImage}
                        className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-2 text-[11px] font-semibold capitalize normal-case tracking-normal text-white outline-none focus:border-indigo-400"
                      >
                        <option value="auto">Auto</option>
                        <option value="opaque">Opaque</option>
                        <option value="transparent">Transparent</option>
                      </select>
                    </label>
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                      Variations
                      <select
                        value={aiImageVariations}
                        onChange={(event) => setAiImageVariations(Number(event.target.value))}
                        disabled={isGeneratingImage}
                        className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-2 text-[11px] font-semibold normal-case tracking-normal text-white outline-none focus:border-indigo-400"
                      >
                        {[1, 2, 3, 4].map((count) => <option key={count} value={count}>{count}</option>)}
                      </select>
                    </label>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <span className="text-[10px] font-semibold text-slate-500">
                      {aiImageModel.includes('sunburst') ? 'Sunburst' : 'Flare'} · {aiImageSize.replace('x', ' × ')}
                    </span>
                    <button
                      type="submit"
                      disabled={isGeneratingImage || aiPrompt.trim().length < 3 || (aiDesignMode !== 'generate' && !aiTargetRef.current)}
                      className="flex items-center gap-2 rounded-xl bg-[#232751] px-3 py-2 text-xs font-black text-white hover:bg-[#30366e] disabled:cursor-not-allowed disabled:bg-slate-800 disabled:text-slate-500"
                    >
                      {isGeneratingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      {isGeneratingImage ? 'Working...' : AI_DESIGN_MODES.find(({ id }) => id === aiDesignMode)?.label}
                    </button>
                  </div>
                </form>
              )}
              <button
                ref={aiLauncherRef}
                type="button"
                onClick={toggleAiPrompt}
                onPointerDown={startAiLauncherDrag}
                onPointerMove={moveAiLauncher}
                onPointerUp={finishAiLauncherDrag}
                onPointerCancel={finishAiLauncherDrag}
                className={`relative flex h-14 w-14 touch-none select-none items-center justify-center border-0 bg-transparent p-0 drop-shadow-xl transition-transform hover:scale-105 ${isCanvasImageOverAi ? 'scale-110 drop-shadow-[0_0_10px_rgba(253,224,71,0.95)]' : ''} ${isGeneratingImage ? 'cursor-wait opacity-70' : 'cursor-grab active:cursor-grabbing'}`}
                title="Drag to move · click to generate an image"
                aria-label="Open AI image generator"
                aria-expanded={isAiPromptOpen}
                aria-disabled={isGeneratingImage}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/favicon.png`}
                  alt=""
                  draggable="false"
                  className={`pointer-events-none h-full w-full object-contain ${isGeneratingImage ? 'animate-pulse' : ''}`}
                />
                {isGeneratingImage && <Loader2 className="pointer-events-none absolute -right-1 -top-1 h-4 w-4 animate-spin text-amber-300" />}
              </button>
            </div>
            {mode === 'annotate' && (
              <div
                ref={drawToolbarRef}
                className="absolute z-20 flex w-44 flex-col gap-3 rounded-2xl border border-slate-700 bg-slate-900/95 p-3 shadow-2xl backdrop-blur"
                style={drawToolbarPosition
                  ? { left: drawToolbarPosition.x, top: drawToolbarPosition.y }
                  : { left: 16, top: '50%', transform: 'translateY(-50%)' }}
                role="toolbar"
                aria-label="Drawing tools"
              >
                <button
                  type="button"
                  className="flex w-full touch-none select-none items-center justify-between rounded-lg px-1 py-0.5 text-slate-400 hover:bg-slate-800 hover:text-white active:cursor-grabbing"
                  onPointerDown={startDrawToolbarDrag}
                  onPointerMove={moveDrawToolbar}
                  onPointerUp={finishDrawToolbarDrag}
                  onPointerCancel={finishDrawToolbarDrag}
                  title="Drag to move the Draw pad"
                  aria-label="Drag to move the Draw pad"
                >
                  <span className="text-[10px] font-black uppercase tracking-widest">Draw</span>
                  <Move className="h-3.5 w-3.5" />
                </button>
                <div className="grid grid-cols-3 gap-1" aria-label="Drawing tool">
                  <button
                    type="button"
                    onClick={() => setAnnotationTool('pen')}
                    className={`flex flex-col items-center gap-1 rounded-lg px-1 py-2 ${annotationTool === 'pen' ? 'bg-[#232751] text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
                    title="Draw freehand"
                    aria-label="Pen"
                    aria-pressed={annotationTool === 'pen'}
                  >
                    <Pencil className="h-4 w-4" />
                    <span className="text-[9px] font-bold">Pen</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAnnotationTool('eraser')}
                    className={`flex flex-col items-center gap-1 rounded-lg px-1 py-2 ${annotationTool === 'eraser' ? 'bg-[#232751] text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
                    title="Erase annotation strokes"
                    aria-label="Eraser"
                    aria-pressed={annotationTool === 'eraser'}
                  >
                    <Eraser className="h-4 w-4" />
                    <span className="text-[9px] font-bold">Erase</span>
                  </button>
                  {[
                    { id: 'line', label: 'Line', Icon: Minus },
                    { id: 'rectangle', label: 'Rect', Icon: Square },
                    { id: 'circle', label: 'Circle', Icon: Circle },
                    { id: 'triangle', label: 'Triangle', Icon: Triangle }
                  ].map(({ id, label, Icon }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setAnnotationTool(id)}
                      className={`flex flex-col items-center gap-1 rounded-lg px-1 py-2 ${annotationTool === id ? 'bg-[#232751] text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
                      title={`Draw a ${label.toLowerCase()}`}
                      aria-label={label}
                      aria-pressed={annotationTool === id}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-[9px] font-bold">{label}</span>
                    </button>
                  ))}
                </div>

                <div className="h-px w-full bg-slate-700" />

                <div>
                  <p className="mb-2 px-1 text-[10px] font-black uppercase tracking-widest text-slate-400">Stroke</p>
                  <div className="grid grid-cols-4 gap-1" aria-label="Stroke widths">
                    {PEN_SIZES.map((size) => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => updateDrawingStrokeWidth(size)}
                        className={`flex h-8 items-center justify-center rounded-lg transition-colors ${penSize === size ? 'bg-white/15 ring-2 ring-white' : 'hover:bg-slate-800'}`}
                        title={`${size}px stroke`}
                        aria-label={`${size}px stroke`}
                        aria-pressed={penSize === size}
                      >
                        <span className="block rounded-full bg-white" style={{ width: Math.min(size, 16), height: Math.min(size, 16) }} />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="h-px w-full bg-slate-700" />

                <div className="space-y-2">
                  <label className="flex items-center justify-between gap-3 px-1 text-[10px] font-black uppercase tracking-widest text-slate-400">
                    Border
                    <span className="relative h-7 w-12 overflow-hidden rounded-lg border border-slate-600" style={{ backgroundColor: annotationColor }}>
                      <input
                        type="color"
                        value={annotationColor}
                        onChange={(event) => updateDrawingStrokeColor(event.target.value)}
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        aria-label="Choose border color"
                      />
                    </span>
                  </label>
                  <div className="grid grid-cols-6 gap-1 px-1" aria-label="Border color presets">
                    {ANNOTATION_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => updateDrawingStrokeColor(color)}
                        className={`h-5 w-5 rounded-full border transition-transform hover:scale-110 ${annotationColor === color ? 'border-white ring-1 ring-white/50' : 'border-slate-600'}`}
                        style={{ backgroundColor: color }}
                        title={`Use border ${color}`}
                        aria-label={`Use border color ${color}`}
                        aria-pressed={annotationColor === color}
                      />
                    ))}
                  </div>
                  <label className={`flex items-center justify-between gap-3 rounded-lg px-1 py-1 text-[10px] font-black uppercase tracking-widest ${annotationTool === 'line' || annotationTool === 'pen' || annotationTool === 'eraser' ? 'text-slate-600' : 'text-slate-400'}`}>
                    Fill
                    <span className="relative h-7 w-12 overflow-hidden rounded-lg border border-slate-600" style={{ backgroundColor: shapeFillTransparent ? 'transparent' : shapeFillColor }}>
                      <input
                        type="color"
                        value={shapeFillColor}
                        onChange={(event) => updateDrawingFillColor(event.target.value)}
                        disabled={annotationTool === 'line' || annotationTool === 'pen' || annotationTool === 'eraser'}
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                        aria-label="Choose fill color"
                      />
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={toggleTransparentFill}
                    disabled={annotationTool === 'line' || annotationTool === 'pen' || annotationTool === 'eraser'}
                    className={`w-full rounded-lg border px-2 py-1.5 text-[10px] font-bold transition-colors ${shapeFillTransparent ? 'border-indigo-400 bg-indigo-400/10 text-indigo-200' : 'border-slate-700 text-slate-400 hover:bg-slate-800'} disabled:cursor-not-allowed disabled:opacity-40`}
                    aria-pressed={shapeFillTransparent}
                  >
                    {shapeFillTransparent ? 'Transparent fill' : 'Make fill transparent'}
                  </button>
                </div>
              </div>
            )}
            {assets.length === 0 && !selectedObject && mode !== 'annotate' && (
              <button type="button" onClick={() => fileInputRef.current?.click()} className="pointer-events-auto absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-400 bg-white/90 px-8 py-7 text-center shadow-xl backdrop-blur">
                <span className="rounded-full bg-[#232751]/10 p-3 text-[#232751]"><Upload className="h-7 w-7" /></span>
                <span className="font-semibold text-slate-800">Upload or drop an image</span>
                <span className="text-xs text-slate-500">You can also paste from the clipboard</span>
              </button>
            )}

            <div className="absolute bottom-4 right-4 z-10 flex flex-col gap-2">
              <div className="flex flex-col rounded-xl border border-slate-700 bg-slate-900/95 p-1 shadow-xl">
                <button
                  type="button"
                  onClick={undoCanvas}
                  disabled={!historyStatus.canUndo}
                  className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white disabled:cursor-not-allowed disabled:text-slate-700 disabled:hover:bg-transparent"
                  title="Undo"
                  aria-label="Undo"
                >
                  <Undo2 className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={redoCanvas}
                  disabled={!historyStatus.canRedo}
                  className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white disabled:cursor-not-allowed disabled:text-slate-700 disabled:hover:bg-transparent"
                  title="Redo"
                  aria-label="Redo"
                >
                  <Redo2 className="h-4 w-4" />
                </button>
              </div>
              <div className="flex flex-col rounded-xl border border-slate-700 bg-slate-900/95 p-1 shadow-xl">
                <button type="button" onClick={() => changeZoom(zoom * 1.2)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white" title="Zoom in"><ZoomIn className="h-4 w-4" /></button>
                <span className="border-y border-slate-800 px-1 py-2 text-center text-[10px] font-bold text-slate-400">{Math.round(zoom * 100)}%</span>
                <button type="button" onClick={() => changeZoom(zoom / 1.2)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white" title="Zoom out"><ZoomOut className="h-4 w-4" /></button>
              </div>
              <button type="button" onClick={resetZoom} className="rounded-xl border border-slate-700 bg-slate-900/95 p-3 text-slate-400 shadow-xl hover:bg-slate-800 hover:text-white" title="Reset zoom"><Maximize className="h-4 w-4" /></button>
              <button type="button" onClick={toggleFullscreen} className="rounded-xl border border-slate-700 bg-slate-900/95 p-3 text-slate-400 shadow-xl hover:bg-slate-800 hover:text-white" title={isFullscreen ? 'Exit fullscreen' : 'Open fullscreen'} aria-label={isFullscreen ? 'Exit fullscreen' : 'Open fullscreen'}>
                {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            <span className="normal-case tracking-normal text-slate-400">{message}</span>
            <div className="flex flex-wrap items-center gap-4">
              <span className="flex items-center gap-1"><Move className="h-3 w-3" /> Drag to move</span>
              <span className="flex items-center gap-1"><Hand className="h-3 w-3" /> Space + drag to pan</span>
              <span className="flex items-center gap-1"><ZoomIn className="h-3 w-3" /> Scroll to zoom</span>
              <span className="flex items-center gap-1"><Maximize2 className="h-3 w-3" /> Handles resize</span>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CanvasEditor;
